import React, { useState, useEffect } from 'react';
import { Tree } from 'primereact/tree';
import { Button } from 'primereact/button';
import { Row } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { getProjectQuestionsBank, setCurrentQuestion, setAddQuestionModalVisibility, deleteQuestion, setQuestionsBank } from './vcompSlice';
import ImageButton from '../buttons/ImageButton';
import addIcon from '../../assets/images/addIconGray.png';
import addIconFocus from '../../assets/images/addIconOrange.png';
import deleteIcon from '../../assets/images/delete.png';
import deleteIconFocus from '../../assets/images/delete-orange.png';
import editIcon from '../../assets/images/penGray.png';
import editIconFocus from '../../assets/images/penOrange.png';
import AddQuestionsModal from './AddQuestionModal';
import MobileAddQuestionModal from './createProjectModalComponents/mobile/MobileAddQuestionModal';
import Langs from '../../app/lang/langs';
import { showErrorToast } from '../../app/utils/helpers';

/* ── MUI imports (mobile only) ─────────────────────────────────── */
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import MobileConfirmDialog from './createProjectModalComponents/mobile/MobileConfirmDialog';
import styles from './VcompCompnents.module.css';

/* ── Constants ─────────────────────────────────────────────────── */
const ORANGE = "#FF9015";
const ORANGE_GLOW = "rgba(255, 144, 21, 0.35)";

/* ── Keyframes (injected once) ─────────────────────────────────── */
const mobileQKeyframesId = "mobile-q-keyframes";
if (typeof document !== "undefined" && !document.getElementById(mobileQKeyframesId)) {
    const style = document.createElement("style");
    style.id = mobileQKeyframesId;
    style.textContent = `
        @keyframes mq-fadeSlideUp {
            from { opacity: 0; transform: translateY(14px) scale(0.97); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes mq-pulseGlow {
            0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255,144,21,0.18); }
            50%      { transform: scale(1.06); box-shadow: 0 0 20px 4px rgba(255,144,21,0.12); }
        }
        @keyframes mq-shimmer {
            0%   { background-position: -200% 0; }
            100% { background-position: 200% 0; }
        }
        @keyframes mq-expandChevron {
            from { transform: rotate(0deg); }
            to   { transform: rotate(180deg); }
        }
    `;
    document.head.appendChild(style);
}

/* ── Mobile Question Card ──────────────────────────────────────── */
function MobileQuestionCard({ question, index, onEdit, onDelete }) {
    const [expanded, setExpanded] = useState(false);
    const rawAnswers = question.correct_answers || [];
    const correctIds = (Array.isArray(rawAnswers) ? rawAnswers : []).map((a) =>
        typeof a === "object" ? a.id : a
    );

    return (
        <Box
            sx={{
                /* Glassmorphic surface */
                background: "rgba(255, 255, 255, 0.72)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                borderRadius: "18px",
                border: "1px solid",
                borderColor: expanded
                    ? "rgba(255, 144, 21, 0.30)"
                    : "rgba(255, 144, 21, 0.08)",
                overflow: "hidden",
                transition: "all 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: expanded
                    ? `0 8px 32px rgba(255, 144, 21, 0.12), 0 2px 8px rgba(255, 144, 21, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.6)`
                    : `0 2px 8px rgba(255, 144, 21, 0.05), 0 1px 3px rgba(0, 0, 0, 0.03), inset 0 1px 0 rgba(255, 255, 255, 0.5)`,
                /* Entrance animation — staggered by index */
                animation: `mq-fadeSlideUp 450ms cubic-bezier(0, 0, 0.2, 1) ${index * 60}ms both`,
                "&:active": {
                    transform: "scale(0.985)",
                },
            }}
        >
            {/* ── Header row ── */}
            <Box
                onClick={() => setExpanded((prev) => !prev)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2.25,
                    py: 2,
                    cursor: "pointer",
                    WebkitTapHighlightColor: "transparent",
                    transition: "background-color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:active": {
                        backgroundColor: "rgba(255, 144, 21, 0.04)",
                    },
                }}
            >
                {/* Question number badge */}
                <Box
                    sx={{
                        minWidth: 34,
                        height: 34,
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "linear-gradient(135deg, rgba(255, 144, 21, 0.15), rgba(255, 193, 0, 0.10))",
                        border: "1px solid rgba(255, 144, 21, 0.12)",
                        flexShrink: 0,
                        boxShadow: "0 2px 8px rgba(255, 144, 21, 0.08)",
                        transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.82rem",
                            fontWeight: 700,
                            color: ORANGE,
                            letterSpacing: "-0.02em",
                        }}
                    >
                        {index + 1}
                    </Typography>
                </Box>

                {/* Question text */}
                <Typography
                    sx={{
                        flex: 1,
                        fontSize: "0.93rem",
                        fontWeight: 550,
                        lineHeight: 1.55,
                        color: "rgba(30, 30, 30, 0.88)",
                        letterSpacing: "-0.01em",
                        display: "-webkit-box",
                        WebkitLineClamp: expanded ? "unset" : 2,
                        WebkitBoxOrient: "vertical",
                        overflow: expanded ? "visible" : "hidden",
                        wordBreak: "break-word",
                        transition: "color 200ms ease",
                    }}
                >
                    {question.question}
                </Typography>

                {/* Actions + expand */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        flexShrink: 0,
                    }}
                >
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit(question);
                        }}
                        sx={{
                            color: "rgba(0, 0, 0, 0.38)",
                            width: 38,
                            height: 38,
                            borderRadius: "12px",
                            backdropFilter: "blur(4px)",
                            transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                                color: ORANGE,
                                backgroundColor: "rgba(255, 144, 21, 0.10)",
                                boxShadow: `0 0 12px rgba(255, 144, 21, 0.15)`,
                                transform: "scale(1.08)",
                            },
                            "&:active": { transform: "scale(0.92)" },
                        }}
                    >
                        <EditOutlinedIcon sx={{ fontSize: 19 }} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(question.id);
                        }}
                        sx={{
                            color: "rgba(0, 0, 0, 0.38)",
                            width: 38,
                            height: 38,
                            borderRadius: "12px",
                            backdropFilter: "blur(4px)",
                            transition: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                                color: "#e53935",
                                backgroundColor: "rgba(229, 57, 53, 0.08)",
                                boxShadow: "0 0 12px rgba(229, 57, 53, 0.12)",
                                transform: "scale(1.08)",
                            },
                            "&:active": { transform: "scale(0.92)" },
                        }}
                    >
                        <DeleteOutlineIcon sx={{ fontSize: 19 }} />
                    </IconButton>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            borderRadius: "8px",
                            transition: "all 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                        }}
                    >
                        <ExpandMoreIcon sx={{ fontSize: 20, color: "rgba(0, 0, 0, 0.30)", transition: "color 200ms ease" }} />
                    </Box>
                </Box>
            </Box>

            {/* ── Expandable options section ── */}
            <Collapse in={expanded} timeout={350} easing="cubic-bezier(0.4, 0, 0.2, 1)">
                <Box
                    sx={{
                        px: 2.25,
                        pb: 2.25,
                        pt: 0.5,
                    }}
                >
                    <Divider
                        sx={{
                            mb: 1.75,
                            borderColor: "rgba(255, 144, 21, 0.10)",
                            borderWidth: "0.5px",
                        }}
                    />

                    {/* Options count chip */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <Chip
                            label={`${(question.options || []).length} option${(question.options || []).length !== 1 ? "s" : ""}`}
                            size="small"
                            sx={{
                                height: 26,
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                borderRadius: "8px",
                                backgroundColor: "rgba(255, 144, 21, 0.07)",
                                color: "rgba(180, 100, 0, 0.7)",
                                border: "1px solid rgba(255, 144, 21, 0.10)",
                                letterSpacing: "0.01em",
                            }}
                        />
                        {correctIds.length > 0 && (
                            <Chip
                                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                label={`${correctIds.length} correcte${correctIds.length !== 1 ? "s" : ""}`}
                                size="small"
                                sx={{
                                    height: 26,
                                    fontSize: "0.72rem",
                                    fontWeight: 600,
                                    borderRadius: "8px",
                                    backgroundColor: "rgba(76, 175, 80, 0.08)",
                                    color: "#2e7d32",
                                    border: "1px solid rgba(76, 175, 80, 0.12)",
                                    "& .MuiChip-icon": { color: "#4caf50" },
                                }}
                            />
                        )}
                    </Box>

                    {/* Option items */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.85 }}>
                        {(question.options || []).map((option, optIdx) => {
                            const isCorrect = correctIds.includes(option.id);
                            return (
                                <Box
                                    key={option.id || optIdx}
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 1.25,
                                        py: 1,
                                        px: 1.5,
                                        borderRadius: "12px",
                                        backgroundColor: isCorrect
                                            ? "rgba(76, 175, 80, 0.06)"
                                            : "rgba(255, 255, 255, 0.5)",
                                        border: "1px solid",
                                        borderColor: isCorrect
                                            ? "rgba(76, 175, 80, 0.18)"
                                            : "rgba(0, 0, 0, 0.04)",
                                        backdropFilter: "blur(8px)",
                                        transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                                        animation: `mq-fadeSlideUp 300ms cubic-bezier(0, 0, 0.2, 1) ${optIdx * 40 + 100}ms both`,
                                    }}
                                >
                                    {isCorrect ? (
                                        <CheckCircleIcon
                                            sx={{
                                                fontSize: 18,
                                                color: "#4caf50",
                                                mt: "3px",
                                                flexShrink: 0,
                                                filter: "drop-shadow(0 1px 3px rgba(76,175,80,0.25))",
                                            }}
                                        />
                                    ) : (
                                        <RadioButtonUncheckedIcon
                                            sx={{
                                                fontSize: 18,
                                                color: "rgba(0, 0, 0, 0.20)",
                                                mt: "3px",
                                                flexShrink: 0,
                                            }}
                                        />
                                    )}
                                    <Typography
                                        sx={{
                                            fontSize: "0.85rem",
                                            lineHeight: 1.55,
                                            color: isCorrect
                                                ? "#2e7d32"
                                                : "rgba(30, 30, 30, 0.62)",
                                            fontWeight: isCorrect ? 550 : 400,
                                            wordBreak: "break-word",
                                            letterSpacing: "-0.005em",
                                        }}
                                    >
                                        {option.value || option.label || option}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
}

/* ── Mobile Empty State ────────────────────────────────────────── */
function MobileEmptyState() {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 10,
                px: 3,
                textAlign: "center",
                animation: "mq-fadeSlideUp 500ms cubic-bezier(0, 0, 0.2, 1) both",
            }}
        >
            {/* Pulsing icon container */}
            <Box
                sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "20px",
                    background: "linear-gradient(135deg, rgba(255, 144, 21, 0.12), rgba(255, 193, 0, 0.08))",
                    border: "1px solid rgba(255, 144, 21, 0.10)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                    animation: "mq-pulseGlow 3s ease-in-out infinite",
                    backdropFilter: "blur(8px)",
                }}
            >
                <QuizOutlinedIcon
                    sx={{
                        fontSize: 34,
                        color: ORANGE,
                        opacity: 0.8,
                        filter: "drop-shadow(0 2px 6px rgba(255,144,21,0.2))",
                    }}
                />
            </Box>
            <Typography
                sx={{
                    fontSize: "1.05rem",
                    fontWeight: 650,
                    color: "rgba(30, 30, 30, 0.75)",
                    mb: 1,
                    letterSpacing: "-0.02em",
                }}
            >
                Aucune question
            </Typography>
            <Typography
                sx={{
                    fontSize: "0.86rem",
                    color: "rgba(0, 0, 0, 0.40)",
                    lineHeight: 1.6,
                    maxWidth: 280,
                    fontWeight: 400,
                }}
            >
                Appuyez sur le bouton + pour ajouter des questions à votre projet.
            </Typography>
        </Box>
    );
}

/* ── Main Component ────────────────────────────────────────────── */
const QuestionsTree = ({ isMobile, mode, setActiveIndex, handleAddDefaultProjectForm, id_project }) => {
    const dispatch = useDispatch();
    const questionsBank = useSelector((state) => state.vcomp.questionsBank);
    const questionDialogVisible = useSelector((state) => state.vcomp.addQuestionModalVisibility);
    const [confirmDeleteDialogVisible, setConfirmDeleteDialogVisible] = useState(false);
    const [selectedQuestionIdToDelete, setSelectedQuestionIdToDelete] = useState(null);
    const [expandedKeys, setExpandedKeys] = useState({});

    const collectAllKeys = (nodeList) => {
        let keys = {};
        (nodeList || []).forEach(node => {
            if (node.children && node.children.length > 0) {
                keys[node.key] = true;
                Object.assign(keys, collectAllKeys(node.children));
            }
        });
        return keys;
    };

    const expandAll = () => {
        setExpandedKeys(collectAllKeys(nodes));
    };

    const collapseAll = () => {
        setExpandedKeys({});
    };


    const handleEditQuestion = (question) => {
        dispatch(setCurrentQuestion(question));
        dispatch(setAddQuestionModalVisibility(true));
    };

    const confirmDeleteQuestion = (id) => {
        setSelectedQuestionIdToDelete(id);
        setConfirmDeleteDialogVisible(true);
    };


    const handleDeleteFromProject = async (id) => {
        try {
            await dispatch(deleteQuestion({ id })).unwrap();
            dispatch(setQuestionsBank(questionsBank.filter((q) => q.id !== id)));
        } catch (error) {
            console.error("Échec de la suppression de la question du projet", error);
        } finally {
            setConfirmDeleteDialogVisible(false);
        }
    };

    const nodeTemplate = (node) => {
        return (
            <div className="node-container">
                <span>{node.data.question}</span>&nbsp;

                <div className="node-button">
                    <Row>
                        <ImageButton
                            image={editIcon}
                            imageFocus={editIconFocus}
                            onClick={() => handleEditQuestion(node.data)}
                            active={true}
                            width={32}
                        />
                        <ImageButton
                            image={deleteIcon}
                            imageFocus={deleteIconFocus}
                            onClick={() => confirmDeleteQuestion(node.key)}
                            active={true}
                            width={32}
                        />
                    </Row>
                </div>
            </div>
        );
    };

    const transformQuestionsToTreeNodes = (questions) => {
        return questions.map((question) => ({
            key: question.id,
            label: question,
            data: question,
            className: 'question',
            children: question.options.map((option) => ({
                key: option.id,
                label: option.value,
                data: option,
                className: 'option',
                selectable: false
            }))
        }));
    };

    const nodes = transformQuestionsToTreeNodes(questionsBank);

    /* ── Mobile rendering ──────────────────────────────────────── */
    if (isMobile === 2) {
        return (
            <Box sx={{ mt: 0, display: "flex", flexDirection: "column", height: "100%" }}>
                {/* Questions list */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        WebkitOverflowScrolling: "touch",
                        px: 1.75,
                        py: 2,
                        /* Thin custom scrollbar with orange tint */
                        "&::-webkit-scrollbar": {
                            width: "4px",
                        },
                        "&::-webkit-scrollbar-track": {
                            background: "transparent",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            background: "rgba(255, 144, 21, 0.18)",
                            borderRadius: "4px",
                        },
                        scrollBehavior: "smooth",
                    }}
                >
                    {questionsBank.length === 0 ? (
                        <MobileEmptyState />
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {questionsBank.map((question, index) => (
                                <MobileQuestionCard
                                    key={question.id || index}
                                    question={question}
                                    index={index}
                                    onEdit={handleEditQuestion}
                                    onDelete={confirmDeleteQuestion}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Question add/edit modal */}
                {questionDialogVisible && <MobileAddQuestionModal />}

                {/* Delete confirmation - MUI Dialog via MobileConfirmDialog */}
                <MobileConfirmDialog
                    open={confirmDeleteDialogVisible}
                    header={<Langs str="DELETE_QUESTION" />}
                    message={<Langs str="DELETE_QUESTION_FROM_PROJECT_MSG" />}
                    acceptLabel={<Langs str="Delete" />}
                    rejectLabel={<Langs str="CANCEL" />}
                    danger
                    onAccept={() => handleDeleteFromProject(selectedQuestionIdToDelete)}
                    onClose={() => setConfirmDeleteDialogVisible(false)}
                />
            </Box>
        );
    }

    /* ── Desktop rendering ─────────────────────────────────────── */
    return (
        <div>
            <div className={styles.questionsDesktopCard}>
                {/* Header with expand/collapse */}
                <div className={styles.questionsDesktopHeader}>
                    <button className={styles.treeActionBtn} onClick={expandAll}>
                        <i className="pi pi-angle-double-down" style={{ marginRight: 4 }} /> Expand All
                    </button>
                    <button className={styles.treeActionBtn} onClick={collapseAll}>
                        <i className="pi pi-angle-double-up" style={{ marginRight: 4 }} /> Collapse All
                    </button>
                </div>

                {/* Tree */}
                <div className={styles.questionsDesktopTreeWrap}>
                    <Tree
                        value={nodes}
                        className="w-full md:w-30rem custom-questions-tree border-0"
                        nodeTemplate={nodeTemplate}
                        expandedKeys={expandedKeys}
                        onToggle={(e) => setExpandedKeys(e.value)}
                    />
                </div>
            </div>
            {
                isMobile !== 2 && mode !== "edit" && (
                    <div>
                        <div style={{ position: 'fixed', bottom: '1.5rem', left: '21rem', width: '7%' }}>
                            <Button label="Previous" onClick={() => setActiveIndex(0)} style={{ width: '100%' }} />
                        </div>
                        <div style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', width: '7%' }}>
                            <Button label="Next" onClick={handleAddDefaultProjectForm} style={{ width: '100%' }} />
                        </div>
                    </div>
                )
            }
            {questionDialogVisible && (isMobile === 2 ? <MobileAddQuestionModal /> : <AddQuestionsModal />)}
            <MobileConfirmDialog
                open={confirmDeleteDialogVisible}
                header={<Langs str="DELETE_QUESTION" />}
                message={<Langs str="DELETE_QUESTION_FROM_PROJECT_MSG" />}
                acceptLabel={<Langs str="Delete" />}
                rejectLabel={<Langs str="CANCEL" />}
                danger
                onAccept={() => handleDeleteFromProject(selectedQuestionIdToDelete)}
                onClose={() => setConfirmDeleteDialogVisible(false)}
            />
        </div>
    );
};

export default QuestionsTree;
